// Third-party
import React from 'react';
import { withFormik, FormikProps, Form, Field, FormikValues, FormikErrors } from 'formik';

// Utils
import { FormattedMessage, formatMessage } from '../../../utils/i18n';
import { validateEmails, FormErrorKeys } from '../../../utils/forms';

// Components
import { PrimaryButton, CancelButton } from '../buttons';
import { FormError } from '../forms';
import Modal from '@/views/components/modals/AccessibleModal';
import { Typography } from '@capintel/component-lib/typography';

// Assets
import connectLogo from '../../../images/connect-graphic-gray.svg';

interface FormProps {
  emails: string;
}

interface ParentProps {
  onSubmit: (emails: string[]) => Promise<void>;
  onCancel: () => void;
  onSuccess: () => void;
  showModal: boolean;
}

type Props = ParentProps & FormikProps<FormProps>;

const canSubmit = (props: Props) => {
  const { values, isSubmitting, errors } = props;
  const hasErrors = Object.keys(errors).length > 0;

  return values.emails && !hasErrors && !isSubmitting;
};

const InvitePeopleModal = (props: Props) => {
  const onCancel = () => {
    props.resetForm();
    props.onCancel();
  };

  const { showModal, setErrors, errors, values } = props;
  return (
    <Modal show={showModal} className="invite-modal" onHide={onCancel} dialogClassName="modal-dialog-centered">
      <Modal.Header className="d-flex flex-column align-items-center">
        <img alt="" src={connectLogo} />
        <Typography variant="headerMd" as="h1">
          <FormattedMessage id="InviteUsersModal.Title" />
        </Typography>
      </Modal.Header>

      <Form onChange={() => setErrors({})}>
        <Modal.Body className="d-flex flex-column">
          <div className="invite-people-modal-labels d-flex justify-content-between">
            <label>
              <FormattedMessage id="InviteUsersModal.EmailAddresses" />
            </label>
            <label>
              <FormattedMessage id="InviteUsersModal.Delimiter" />
            </label>
          </div>

          <Field
            required
            spellCheck="false"
            name="emails"
            component="textarea"
            placeholder={'sarah@capintel.io, \ndavid@capintel.io'}
            data-private
            data-dd-privacy="mask"
          />

          <div className="invite-modal-text">
            <FormattedMessage id="InviteUsersModal.Text" />
          </div>

          {errors.emails && (
            <div className="error-message">
              <FormError name="emails" />

              <ul className="form-error-list">
                {validateEmails(values.emails).invalidEmails.map((email, i) => (
                  <li key={i}>{email}</li>
                ))}
              </ul>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex flex-column align-items-center">
          <PrimaryButton className="submit-button" type="submit" disabled={!canSubmit(props)}>
            <FormattedMessage id="InviteUsersModal.SendInvites" />
          </PrimaryButton>

          <CancelButton onClick={onCancel} />
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const validate = (values: FormikValues): FormikErrors<FormikValues> | void => {
  const errors: FormikErrors<FormikValues> = {};
  const { emails } = values;
  const { validLimit, isValid, invalidEmails } = validateEmails(emails);

  if (isValid && validLimit) {
    return;
  }

  if (!isValid) {
    errors.emails = formatMessage(FormErrorKeys.INVALID_EMAIL, { count: invalidEmails.length });
  } else if (!validLimit) {
    errors.emails = formatMessage(FormErrorKeys.TOO_MANY_EMAILS);
  }

  return errors;
};

const formikWrapper = withFormik<Props, FormProps>({
  validate,
  validateOnBlur: false,
  validateOnChange: false,
  mapPropsToValues: () => ({ emails: '' }),
  handleSubmit: ({ emails }, { props, resetForm, setSubmitting }) => {
    const { validEmails } = validateEmails(emails);
    props
      .onSubmit(validEmails)
      .then(() => {
        resetForm();
        props.onSuccess();
      })
      .finally(() => {
        setSubmitting(false);
      });
  },
});

export default formikWrapper(InvitePeopleModal) as any;
